// Collapsible "Customize" panel underneath the chart. Mutates an in-memory
// ChartCustomization object owned by the parent — persistence is the
// parent's responsibility (loadCustomization / saveCustomization).
//
// Intentionally renders into <details>/<summary> rather than a popover:
// keyboard accessible by default, no portal/escape-handler boilerplate,
// no extra deps. Mónica + Ana operate from the keyboard a lot.
import type { ChartType } from "../../types/chart";
import type { AxisLabelAngle, ChartCustomization, LabelMode, SortMode } from "./customization";
import { PALETTES, type PaletteId } from "./palettes";

interface Props {
  value: ChartCustomization;
  onChange: (next: ChartCustomization) => void;
}

const CHART_TYPES: Array<{ value: ChartType; label: string }> = [
  { value: "pie", label: "Pie" },
  { value: "donut", label: "Donut" },
  { value: "bar", label: "Bar" },
  { value: "horizontal_bar", label: "Horizontal bar" },
  { value: "table", label: "Table only" },
];

const LABEL_MODES: Array<{ value: LabelMode; label: string }> = [
  { value: "count", label: "Count only" },
  { value: "percent", label: "Percent only" },
  { value: "both", label: "Both" },
  { value: "hide", label: "Hide" },
];

const SORT_MODES: Array<{ value: SortMode; label: string }> = [
  { value: "desc", label: "Largest first" },
  { value: "asc", label: "Smallest first" },
  { value: "alpha", label: "Alphabetical" },
];

const AXIS_ANGLES: Array<{ value: AxisLabelAngle; label: string }> = [
  { value: 0, label: "Horizontal" },
  { value: 45, label: "Diagonal (45°)" },
  { value: 90, label: "Vertical (90°)" },
];

export function ChartCustomizationPanel({ value, onChange }: Props) {
  const patch = (delta: Partial<ChartCustomization>) => onChange({ ...value, ...delta });
  return (
    <details className="chart-customization">
      <summary>Customize</summary>
      <div className="chart-customization__grid">
        <Field label="Chart type">
          <select
            value={value.chartType}
            onChange={(e) => patch({ chartType: e.target.value as ChartType })}
            aria-label="Chart type"
          >
            {CHART_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Color palette">
          <select
            value={value.palette}
            onChange={(e) => patch({ palette: e.target.value as PaletteId })}
            aria-label="Color palette"
          >
            {PALETTES.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Labels">
          <select
            value={value.labelMode}
            onChange={(e) => patch({ labelMode: e.target.value as LabelMode })}
            aria-label="Label mode"
          >
            {LABEL_MODES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Sort">
          <select
            value={value.sortMode}
            onChange={(e) => patch({ sortMode: e.target.value as SortMode })}
            aria-label="Sort mode"
          >
            {SORT_MODES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label={`Top N (${value.topN})`}>
          <input
            type="range"
            min={3}
            max={20}
            step={1}
            value={value.topN}
            onChange={(e) => patch({ topN: Number(e.target.value) })}
            aria-label="Top N buckets"
          />
        </Field>

        {value.chartType === "bar" && (
          <Field label="Axis labels">
            <select
              value={value.axisLabelAngle}
              onChange={(e) => patch({ axisLabelAngle: Number(e.target.value) as AxisLabelAngle })}
              aria-label="Axis label angle"
            >
              {AXIS_ANGLES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Legend">
          <label className="chart-customization__toggle">
            <input
              type="checkbox"
              checked={value.showLegend}
              onChange={(e) => patch({ showLegend: e.target.checked })}
            />
            <span>Show legend / table</span>
          </label>
        </Field>
      </div>
    </details>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="chart-customization__field">
      <span className="chart-customization__label">{label}</span>
      {children}
    </label>
  );
}
